// src/pages/groups/GroupsList.jsx
// المدرس: مجموعاته + ملخص النشاط + إنشاء/تعديل/حذف
// الأدمن: كل المجموعات (فلتر بالمدرس) + إنشاء مجموعة لمدرس + حذف
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Search, Trash2, Users } from "lucide-react";
import { useAuthStore } from "../../store/authstore";
import { groupService, getErrorMessage } from "../../services/groupService";
import GroupFormModal from "./GroupFormModal";
import {
  ActivityBar,
  ConfirmDialog,
  DaysSeg,
  EmptyState,
  Legend,
  Pagination,
  Seg,
  Spinner,
  Stat,
  useDebounce,
  useGroupStyles,
  useToast,
} from "./groupsShared";

const STATUS_OPTIONS = [
  ["all", "الكل"],
  ["active", "نشطة"],
  ["inactive", "متوقفة"],
];

function GroupCard({ group, isAdmin, active, onEdit, onDelete }) {
  const total = group.students_count || 0;
  const segments =
    active === undefined
      ? null
      : [
          { tone: "ok", value: active, label: "نشط" },
          { tone: "none", value: Math.max(0, total - active), label: "غير نشط" },
        ];

  return (
    <article className="gp-panel gp-card">
      <div className="gp-card-top">
        <Link to={`/dashboard/groups/${group.id}`} className="gp-link">
          <h3>{group.name}</h3>
        </Link>
        <span className={`gp-badge ${group.is_active ? "ok" : ""}`}>{group.is_active ? "نشطة" : "متوقفة"}</span>
      </div>

      <p>{group.description || "لا يوجد وصف لهذه المجموعة"}</p>

      {segments && total > 0 && (
        <div>
          <ActivityBar segments={segments} />
          <Legend segments={segments} />
        </div>
      )}

      <div className="gp-card-foot">
        <span>
          <Users size={13} style={{ verticalAlign: "-2px", marginInlineEnd: 5 }} />
          {total} طالب
          {isAdmin && <span style={{ display: "block", marginTop: 3 }}>المدرس: {group.teacher_name}</span>}
        </span>
        <span className="gp-actions" style={{ gap: 2 }}>
          {!isAdmin && (
            <button className="gp-icon-btn" onClick={() => onEdit(group)} aria-label={`تعديل ${group.name}`}>
              <Pencil size={15} />
            </button>
          )}
          <button className="gp-icon-btn danger" onClick={() => onDelete(group)} aria-label={`حذف ${group.name}`}>
            <Trash2 size={15} />
          </button>
          <Link to={`/dashboard/groups/${group.id}`} className="gp-btn gp-btn-sm">
            فتح المجموعة
          </Link>
        </span>
      </div>
    </article>
  );
}

export default function GroupsList() {
  useGroupStyles();
  const { user } = useAuthStore();
  const isAdmin = user?.user_type === "admin";
  const [toastNode, notify] = useToast();

  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ count: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const q = useDebounce(search);
  const [status, setStatus] = useState("all");
  const [teacherId, setTeacherId] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [page, setPage] = useState(1);

  const [days, setDays] = useState(7);
  const [dash, setDash] = useState(null);

  const [formGroup, setFormGroup] = useState(null); // null | "new" | group
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, page_size: 12 };
      if (q.trim()) params.search = q.trim();
      if (status !== "all") params.is_active = status === "active";
      if (isAdmin && teacherId) params.teacher_id = teacherId;
      const res = isAdmin ? await groupService.adminListGroups(params) : await groupService.listGroups(params);
      setGroups(res.data);
      setMeta({ count: res.count, totalPages: res.total_pages });
    } catch (e) {
      if (e?.response?.status === 404 && page > 1) {
        setPage(1);
        return;
      }
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [page, q, status, teacherId, isAdmin]);

  const loadDashboard = useCallback(async () => {
    if (isAdmin) return;
    try {
      const res = await groupService.getDashboard(days);
      setDash(res.data);
    } catch {
      setDash(null); // الملخص غير أساسي، والصفحة تكمل بدونه
    }
  }, [days, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!isAdmin) return;
    groupService
      .getTeachers()
      .then((res) => setTeachers(res.data || []))
      .catch(() => setTeachers([]));
  }, [isAdmin]);

  const activeById = useMemo(
    () => Object.fromEntries((dash?.groups || []).map((g) => [g.id, g.active_students])),
    [dash]
  );

  const onSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const onStatus = (v) => {
    setStatus(v);
    setPage(1);
  };
  const onTeacher = (e) => {
    setTeacherId(e.target.value);
    setPage(1);
  };

  const onSaved = (_group, message) => {
    setFormGroup(null);
    notify(message || "تم الحفظ بنجاح");
    load();
    loadDashboard();
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      if (isAdmin) await groupService.adminDeleteGroup(deleteTarget.id);
      else await groupService.deleteGroup(deleteTarget.id);
      notify("تم حذف المجموعة");
      setDeleteTarget(null);
      load();
      loadDashboard();
    } catch (e) {
      notify(getErrorMessage(e), "err");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = Boolean(q.trim()) || status !== "all" || Boolean(teacherId);

  return (
    <div className="gp-root">
      <div className="gp-head">
        <div>
          <h1 className="gp-title">{isAdmin ? "مجموعات الطلبة" : "مجموعاتي"}</h1>
          <p className="gp-sub">
            {isAdmin
              ? "جميع المجموعات التي أنشأها المدرسون. يمكنك إنشاء مجموعة جديدة وربطها بمدرس، أو فتح أي مجموعة، أو حذفها."
              : "نظّم طلابك في مجموعات وتابع تقدمهم ونشاطهم من مكان واحد."}
          </p>
        </div>
        <div className="gp-actions">
          {!isAdmin && (
            <Link className="gp-btn" to="/dashboard/groups/students">
              <Users size={15} /> كل طلابي
            </Link>
          )}
          <button className="gp-btn gp-btn-primary" onClick={() => setFormGroup("new")}>
            <Plus size={15} /> مجموعة جديدة
          </button>
        </div>
      </div>

      {!isAdmin && dash && (
        <>
          <div className="gp-toolbar" style={{ justifyContent: "space-between" }}>
            <span className="gp-sub" style={{ margin: 0 }}>
              نشاط طلابك
            </span>
            <DaysSeg value={days} onChange={setDays} />
          </div>
          <div className="gp-stats">
            <Stat value={dash.groups_count} label="مجموعاتك" />
            <Stat value={dash.total_students} label="إجمالي الطلاب" />
            <Stat value={dash.active_students} label="طلاب نشطون" />
            <Stat value={dash.inactive_students} label="طلاب لم يحلّوا شيئاً" />
          </div>
        </>
      )}

      <div className="gp-toolbar">
        <div className="gp-search">
          <Search size={15} />
          <input
            className="gp-input"
            value={search}
            onChange={onSearch}
            placeholder={isAdmin ? "ابحث باسم المجموعة أو المدرس" : "ابحث باسم المجموعة"}
            aria-label="بحث في المجموعات"
          />
        </div>
        {isAdmin && (
          <select className="gp-select" value={teacherId} onChange={onTeacher} aria-label="فلتر المدرس">
            <option value="">كل المدرسين</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.full_name}
              </option>
            ))}
          </select>
        )}
        <Seg label="حالة المجموعة" value={status} onChange={onStatus} options={STATUS_OPTIONS} />
      </div>

      {error && <div className="gp-error">{error}</div>}

      {loading ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <div className="gp-panel">
          {hasFilters ? (
            <EmptyState title="لا توجد نتائج" hint="جرّب تغيير البحث أو الفلتر." />
          ) : (
            <EmptyState
              title={isAdmin ? "لا توجد مجموعات بعد" : "لم تنشئ أي مجموعة بعد"}
              hint={
                isAdmin
                  ? "أنشئ مجموعة وحدد المدرس المسؤول عنها."
                  : "أنشئ أول مجموعة وأضف إليها طلابك لتتابع تقدمهم."
              }
              action={
                <button className="gp-btn gp-btn-primary" onClick={() => setFormGroup("new")}>
                  <Plus size={15} /> إنشاء مجموعة
                </button>
              }
            />
          )}
        </div>
      ) : (
        <div className="gp-grid">
          {groups.map((g) => (
            <GroupCard
              key={g.id}
              group={g}
              isAdmin={isAdmin}
              active={activeById[g.id]}
              onEdit={setFormGroup}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={meta.totalPages} onChange={setPage} />

      {formGroup && (
        <GroupFormModal
          group={formGroup === "new" ? null : formGroup}
          isAdmin={isAdmin}
          teachers={teachers}
          onClose={() => setFormGroup(null)}
          onSaved={onSaved}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          danger
          title="حذف المجموعة"
          message={`سيتم حذف مجموعة "${deleteTarget.name}" وإزالة عضوية الطلاب منها. حسابات الطلاب وتقدمهم لن يتأثروا.`}
          confirmLabel="حذف المجموعة"
          loading={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {toastNode}
    </div>
  );
}